/**
 * Painel de chat com o Agente Pericial (EW.F3.T08). // ux-neutrality:allow doc-comment
 *
 * Polling externo via prop `messages`. O componente é controlado: // ux-neutrality:allow doc-comment
 * recebe `pendingCriterionId` (do CriteriaPanel) que injeta no próximo // ux-neutrality:allow doc-comment
 * POST e limpa após envio. // ux-neutrality:allow doc-comment
 */
import { useState, type FormEvent } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Blueprint } from '@/components/ui/Blueprint';
import { ptBR } from '@/i18n/pt-BR';
import type { ChatMessageData } from '@/lib/case_chat';

export interface ChatPanelProps {
  messages: ChatMessageData[];
  sending: boolean;
  errorMessage: string | null;
  pendingCriterionId: string | null;
  onSend: (content: string, criterionId: string | null) => void;
}

export function ChatPanel({
  messages,
  sending,
  errorMessage,
  pendingCriterionId,
  onSend,
}: ChatPanelProps): JSX.Element {
  const [input, setInput] = useState('');

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const trimmed = input.trim();
    if (!trimmed || sending) return;
    onSend(trimmed, pendingCriterionId);
    setInput('');
  }

  return (
    <Blueprint as="section" className="chat-panel" aria-label={ptBR.workspaceCaso.chatTitle}>
      <header>
        <h3>{ptBR.workspaceCaso.chatTitle}</h3>
      </header>
      <ol
        className="chat-messages"
        data-testid="chat-messages"
        aria-live="polite"
        aria-atomic="false"
      >
        {messages.length === 0 && <li className="chat-empty">{ptBR.workspaceCaso.chatEmpty}</li>}
        {messages.map((msg) => (
          <li
            key={msg.id}
            className={`chat-message chat-${msg.role}`}
            data-testid={`chat-msg-${msg.role}`}
          >
            {msg.role === 'assistant' ? (
              <div className="chat-message-body prose prose-sm max-w-none">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown>
              </div>
            ) : (
              <p className="chat-message-body">{msg.content}</p>
            )}
            {msg.criterion_id && <small className="chat-criterion">{msg.criterion_id}</small>}
          </li>
        ))}
        {/* EM.F1.T03: o Agente pode levar dezenas de segundos. Indicador
            persistente na conversa deixa a espera explícita (vs. só o label
            do botão), evitando a leitura de "travou / sem conexão". */}
        {sending && (
          <li className="chat-message chat-assistant chat-pending" data-testid="chat-pending">
            <p className="chat-message-body">{ptBR.workspaceCaso.chatProcessing}</p>
          </li>
        )}
      </ol>
      {errorMessage && (
        <div role="alert" className="chat-error">
          {errorMessage}
        </div>
      )}
      <form className="chat-input-form" onSubmit={handleSubmit}>
        {pendingCriterionId && (
          <p className="chat-criterion-hint" data-testid="chat-criterion-hint">
            {pendingCriterionId}
          </p>
        )}
        <div
          className="mb-2 flex flex-wrap gap-1.5"
          role="group"
          aria-label={ptBR.workspaceCaso.chatChipsLabel}
          data-testid="chat-chips"
        >
          {[
            ptBR.workspaceCaso.chatChip1,
            ptBR.workspaceCaso.chatChip2,
            ptBR.workspaceCaso.chatChip3,
          ].map((suggestion, idx) => (
            <button
              key={idx}
              type="button"
              onClick={() => setInput(suggestion)}
              disabled={sending}
              data-testid={`chat-chip-${idx + 1}`}
              className="border border-border px-3 py-1 text-caption text-fg-muted transition-colors duration-fast ease-sc hover:border-accent hover:text-fg disabled:cursor-not-allowed disabled:opacity-50"
            >
              {suggestion}
            </button>
          ))}
        </div>
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={ptBR.workspaceCaso.chatPlaceholder}
          aria-label={ptBR.workspaceCaso.chatPlaceholder}
          rows={3}
          disabled={sending}
          data-testid="chat-input"
        />
        <button type="submit" disabled={sending || input.trim().length === 0}>
          {sending ? ptBR.workspaceCaso.chatSending : ptBR.workspaceCaso.chatSend}
        </button>
      </form>
    </Blueprint>
  );
}
